import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  InputLabel,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {useEffect, useState} from 'react';
import {Amenity} from 'src/apis/autoGenerate/types/amenity';
import {Apartment} from 'src/apis/autoGenerate/types/apartment';
import {amenityServiceCreate, amenityServiceUpdate} from 'src/apis/autoGenerate/services';
import message from 'src/components/Message';
import {useOrgId} from 'src/hooks';
import SearchApartments from 'src/components/SearchApartment';

export interface Props {
  open: boolean;
  /** New-style close handler */
  onClose?: () => void;
  /** New-style success callback */
  onSuccess?: () => void;
  /** Amenity to edit (new-style) */
  amenity?: Amenity;
  /** Default apartment (new-style) */
  defaultApartment?: Apartment | null;
  /** Legacy: apartment for creation */
  apartment?: Apartment;
  /** Legacy: state setter to close */
  setOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  /** Legacy: confirm callback */
  handleConfirm?: VoidFunction;
}

const DURATION_PRESETS = [30, 60, 90, 120, 180, 240, 360, 480, 720, 1440];

export default function AddAmenityModal(props: Props) {
  const {
    open,
    onClose,
    onSuccess,
    amenity,
    defaultApartment,
    apartment: legacyApartment,
    setOpen: legacySetOpen,
    handleConfirm: legacyHandleConfirm,
  } = props;

  const {orgId} = useOrgId();
  const isEdit = !!amenity?.id;
  const initialApartment = defaultApartment || legacyApartment || null;

  const [name, setName] = useState('');
  const [apartment, setApartment] = useState<Apartment | null>(null);
  const [openHoursStart, setOpenHoursStart] = useState('06:00');
  const [openHoursEnd, setOpenHoursEnd] = useState('22:00');
  const [isOpen24Hours, setIsOpen24Hours] = useState(false);
  const [lastCustomOpenHours, setLastCustomOpenHours] = useState({start: '06:00', end: '22:00'});
  const [capacity, setCapacity] = useState('10');
  const [duration, setDuration] = useState<number>(60);
  const [maxBookingsPerDay, setMaxBookingsPerDay] = useState('2');
  const [maxBookingDays, setMaxBookingDays] = useState('7');
  const [loading, setLoading] = useState(false);

  const handleClose = () => {
    if (onClose) onClose();
    if (legacySetOpen) legacySetOpen(false);
  };

  useEffect(() => {
    if (open) {
      if (amenity) {
        const startTime = amenity.openHours?.start || '06:00';
        const endTime = amenity.openHours?.end || '22:00';
        const is24Hours = startTime === '00:00' && endTime === '00:00';
        setName(amenity.name || '');
        setOpenHoursStart(startTime);
        setOpenHoursEnd(endTime);
        setIsOpen24Hours(is24Hours);
        setLastCustomOpenHours(is24Hours ? {start: '06:00', end: '22:00'} : {start: startTime, end: endTime});
        setCapacity(String(amenity.capacity || 10));
        setDuration(amenity.durationOptions?.[0] || 60);
        setMaxBookingsPerDay(String(amenity.maxBookingsPerUserPerDay || 2));
        setMaxBookingDays(String(amenity.maxBookingDays || 7));
      } else {
        setName('');
        setOpenHoursStart('06:00');
        setOpenHoursEnd('22:00');
        setIsOpen24Hours(false);
        setLastCustomOpenHours({start: '06:00', end: '22:00'});
        setCapacity('10');
        setDuration(60);
        setMaxBookingsPerDay('2');
        setMaxBookingDays('7');
      }
      setApartment(initialApartment);
    }
  }, [open, amenity]);

  const handleSubmit = async () => {
    if (!name.trim()) {
      message.error('Name is required');
      return;
    }
    const aptId = apartment?.id || amenity?.apartmentId;
    if (!aptId) {
      message.error('Property is required');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        amenity: {
          name: name.trim(),
          orgId,
          apartmentId: aptId,
          openHours: {start: openHoursStart, end: openHoursEnd},
          capacity: parseInt(capacity) || 10,
          durationOptions: [duration],
          maxBookingsPerUserPerDay: parseInt(maxBookingsPerDay) || 2,
          maxBookingDays: parseInt(maxBookingDays) || 7,
        },
      };

      if (isEdit && amenity?.id) {
        await amenityServiceUpdate(amenity.id, payload);
        message.success('Amenity updated');
      } else {
        await amenityServiceCreate(payload);
        message.success('Amenity created');
      }
      if (onSuccess) onSuccess();
      if (legacyHandleConfirm) legacyHandleConfirm();
      handleClose();
    } catch {
      message.error(`Failed to ${isEdit ? 'update' : 'create'} amenity`);
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (mins: number) => {
    if (mins >= 1440) return `${mins / 1440}d`;
    if (mins >= 60) return `${mins / 60}h`;
    return `${mins}m`;
  };

  const handleToggle24Hours = (checked: boolean) => {
    if (checked) {
      setLastCustomOpenHours({start: openHoursStart, end: openHoursEnd});
      setOpenHoursStart('00:00');
      setOpenHoursEnd('00:00');
      setIsOpen24Hours(true);
      return;
    }

    setOpenHoursStart(lastCustomOpenHours.start);
    setOpenHoursEnd(lastCustomOpenHours.end);
    setIsOpen24Hours(false);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Edit Amenity' : 'Add Amenity'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{mt: 1}}>
          <TextField
            label="Name"
            value={name}
            onChange={e => setName(e.target.value)}
            fullWidth
            required
          />

          {!isEdit && !initialApartment && (
            <SearchApartments onChange={(apt: Apartment | null) => setApartment(apt)} />
          )}

          <Stack direction="column" spacing={0.5}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isOpen24Hours}
                  onChange={e => handleToggle24Hours(e.target.checked)}
                />
              }
              label="24 Hours Open"
            />
            <Stack direction="row" spacing={2}>
              <TextField
                label="Open Time"
                type="time"
                value={openHoursStart}
                onChange={e => {
                  setOpenHoursStart(e.target.value);
                  setLastCustomOpenHours(prev => ({...prev, start: e.target.value}));
                }}
                InputLabelProps={{shrink: true}}
                fullWidth
                disabled={isOpen24Hours}
              />
              <TextField
                label="Close Time"
                type="time"
                value={openHoursEnd}
                onChange={e => {
                  setOpenHoursEnd(e.target.value);
                  setLastCustomOpenHours(prev => ({...prev, end: e.target.value}));
                }}
                InputLabelProps={{shrink: true}}
                fullWidth
                disabled={isOpen24Hours}
              />
            </Stack>
            {(() => {
              const tz = apartment?.timeZone;
              const name = apartment?.name;
              if (!tz && !name) {
                return (
                  <Typography variant="caption" color="text.secondary">
                    Entered in the property's local time.
                  </Typography>
                );
              }
              return (
                <Typography variant="caption" color="text.secondary">
                  Entered in {name ? `${name}'s` : "the property's"} local time
                  {tz ? ` (${tz})` : ''}.
                </Typography>
              );
            })()}
          </Stack>

          <TextField
            label="Capacity (max concurrent reservations)"
            type="number"
            value={capacity}
            onChange={e => setCapacity(e.target.value)}
            fullWidth
            inputProps={{min: 1}}
          />

          <Box>
            <InputLabel sx={{mb: 1, fontSize: '0.875rem'}}>Slot Duration</InputLabel>
            <ToggleButtonGroup
              value={duration}
              exclusive
              onChange={(_, val) => { if (val !== null) setDuration(val); }}
              size="small"
              sx={{flexWrap: 'wrap', gap: 0.5}}
            >
              {DURATION_PRESETS.map(d => (
                <ToggleButton key={d} value={d} sx={{textTransform: 'none', px: 1.5}}>
                  {formatDuration(d)}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          <Stack direction="row" spacing={2}>
            <TextField
              label="Max Bookings Per User Per Day"
              type="number"
              value={maxBookingsPerDay}
              onChange={e => setMaxBookingsPerDay(e.target.value)}
              fullWidth
              inputProps={{min: 1}}
            />
            <TextField
              label="Max Advance Booking Days"
              type="number"
              value={maxBookingDays}
              onChange={e => setMaxBookingDays(e.target.value)}
              fullWidth
              inputProps={{min: 1, max: 30}}
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{px: 3, pb: 2}}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={loading}>
          {isEdit ? 'Save' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
